import { useState, useEffect, useCallback } from 'react';
import {
  getSponsors,
  createSponsor,
  updateSponsor,
  getSponsorById,
  getContracts,
  createContract,
  getContractById,
  runFeasibilityCheck,
  activateContract,
  pauseContract,
  resumeContract,
  getCategories,
  createCategory,
  updateCategory,
  queryInventory,
  getSponsorDeliveryReport,
  getUnfilledSlotsReport,
  runWeeklyAssignmentJob,
  uploadMedia,
} from '../Services/adminApi';

/**
 * Custom hook for sponsorship API operations
 * Provides a unified interface for all sponsorship-related API calls
 */
export const useSponsorshipApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Wrapper for API calls with loading and error handling
   * @param {Function} apiCall - API function to call
   * @param {...any} args - Arguments to pass to API function
   * @returns {Promise<any>} API response
   */
  const executeApiCall = useCallback(async (apiCall, ...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiCall(...args);
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Sponsor operations
  const sponsors = {
    list: useCallback((params) => executeApiCall(getSponsors, params), [executeApiCall]),
    create: useCallback((data) => executeApiCall(createSponsor, data), [executeApiCall]),
    update: useCallback((id, data) => executeApiCall(updateSponsor, id, data), [executeApiCall]),
    getById: useCallback((id) => executeApiCall(getSponsorById, id), [executeApiCall]),
  };

  // Contract operations
  const contracts = {
    list: useCallback((params) => executeApiCall(getContracts, params), [executeApiCall]),
    create: useCallback((data) => executeApiCall(createContract, data), [executeApiCall]),
    getById: useCallback((id) => executeApiCall(getContractById, id), [executeApiCall]),
    runFeasibilityCheck: useCallback((id) => executeApiCall(runFeasibilityCheck, id), [executeApiCall]),
    activate: useCallback((id) => executeApiCall(activateContract, id), [executeApiCall]),
    pause: useCallback((id) => executeApiCall(pauseContract, id), [executeApiCall]),
    resume: useCallback((id) => executeApiCall(resumeContract, id), [executeApiCall]),
  };

  // Category operations
  const categories = {
    list: useCallback(() => executeApiCall(getCategories), [executeApiCall]),
    create: useCallback((data) => executeApiCall(createCategory, data), [executeApiCall]),
    update: useCallback((id, data) => executeApiCall(updateCategory, id, data), [executeApiCall]),
  };

  // Inventory operations
  const inventory = {
    query: useCallback((params) => executeApiCall(queryInventory, params), [executeApiCall]),
  };

  // Reports operations
  const reports = {
    getSponsorDelivery: useCallback((sponsorId, params) => 
      executeApiCall(getSponsorDeliveryReport, sponsorId, params), [executeApiCall]),
    getUnfilledSlots: useCallback((params) => executeApiCall(getUnfilledSlotsReport, params), [executeApiCall]),
  };

  // Assignment operations
  const assignments = {
    runWeeklyJob: useCallback(() => executeApiCall(runWeeklyAssignmentJob), [executeApiCall]),
  };

  // Media operations
  const media = {
    upload: useCallback((formData) => executeApiCall(uploadMedia, formData), [executeApiCall]),
  };

  return {
    loading,
    error,
    setError,
    sponsors,
    contracts,
    categories,
    inventory,
    reports,
    assignments,
    media,
  };
};

/**
 * Custom hook for sponsors data management
 * Handles sponsor listing, creation, updates, and caching
 */
export const useSponsors = (initialParams = {}) => {
  const [sponsors, setSponsors] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 0,
    limit: 20,
  });
  const [filters, setFilters] = useState(initialParams);
  const { sponsors: sponsorsApi, loading, error } = useSponsorshipApi();

  const loadSponsors = useCallback(async (params = {}) => {
    try {
      const response = await sponsorsApi.list({ ...filters, ...params });
      setSponsors(response.sponsors || []);
      setPagination({
        total: response.total || 0,
        page: response.page || 1,
        totalPages: response.totalPages || 0,
        limit: response.limit || 20,
      });
    } catch (err) {
      console.error('Failed to load sponsors:', err);
    }
  }, [sponsorsApi, filters]);

  const createSponsor = useCallback(async (data) => {
    const newSponsor = await sponsorsApi.create(data);
    await loadSponsors(); // Refresh list
    return newSponsor;
  }, [sponsorsApi, loadSponsors]);

  const updateSponsor = useCallback(async (id, data) => {
    const updatedSponsor = await sponsorsApi.update(id, data);
    await loadSponsors(); // Refresh list
    return updatedSponsor;
  }, [sponsorsApi, loadSponsors]);

  const deleteSponsor = useCallback(async (id) => {
    // Assuming there's a delete endpoint or we use update with is_active: false
    await sponsorsApi.update(id, { is_active: false });
    await loadSponsors(); // Refresh list
  }, [sponsorsApi, loadSponsors]);

  useEffect(() => {
    loadSponsors();
  }, [loadSponsors]);

  return {
    sponsors,
    pagination,
    filters,
    setFilters,
    loading,
    error,
    loadSponsors,
    createSponsor,
    updateSponsor,
    deleteSponsor,
  };
};

/**
 * Custom hook for contracts data management
 * Handles contract listing, creation, updates, and status changes
 */
export const useContracts = (initialParams = {}) => {
  const [contracts, setContracts] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 0,
    limit: 20,
  });
  const [filters, setFilters] = useState(initialParams);
  const { contracts: contractsApi, loading, error } = useSponsorshipApi();

  const loadContracts = useCallback(async (params = {}) => {
    try {
      const response = await contractsApi.list({ ...filters, ...params });
      setContracts(response.contracts || []);
      setPagination({
        total: response.total || 0,
        page: response.page || 1,
        totalPages: response.totalPages || 0,
        limit: response.limit || 20,
      });
    } catch (err) {
      console.error('Failed to load contracts:', err);
    }
  }, [contractsApi, filters]);

  const createContract = useCallback(async (data) => {
    const newContract = await contractsApi.create(data);
    await loadContracts(); // Refresh list
    return newContract;
  }, [contractsApi, loadContracts]);

  const updateContractStatus = useCallback(async (id, action) => {
    switch (action) {
      case 'activate':
        await contractsApi.activate(id);
        break;
      case 'pause':
        await contractsApi.pause(id);
        break;
      case 'resume':
        await contractsApi.resume(id);
        break;
      default:
        throw new Error(`Unknown action: ${action}`);
    }
    await loadContracts(); // Refresh list
  }, [contractsApi, loadContracts]);

  const runFeasibilityCheck = useCallback(async (id) => {
    const result = await contractsApi.runFeasibilityCheck(id);
    await loadContracts(); // Refresh list to get updated feasibility data
    return result;
  }, [contractsApi, loadContracts]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  return {
    contracts,
    pagination,
    filters,
    setFilters,
    loading,
    error,
    loadContracts,
    createContract,
    updateContractStatus,
    runFeasibilityCheck,
  };
};

/**
 * Custom hook for categories data management
 */
export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const { categories: categoriesApi, loading, error } = useSponsorshipApi();

  const loadCategories = useCallback(async () => {
    try {
      const response = await categoriesApi.list();
      setCategories(response || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, [categoriesApi]);

  const createCategory = useCallback(async (data) => {
    const newCategory = await categoriesApi.create(data);
    await loadCategories(); // Refresh list
    return newCategory;
  }, [categoriesApi, loadCategories]);

  const updateCategory = useCallback(async (id, data) => {
    const updatedCategory = await categoriesApi.update(id, data);
    await loadCategories(); // Refresh list
    return updatedCategory;
  }, [categoriesApi, loadCategories]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  return {
    categories,
    loading,
    error,
    loadCategories,
    createCategory,
    updateCategory,
  };
};

/**
 * Custom hook for inventory data management
 */
export const useInventory = () => {
  const [inventoryData, setInventoryData] = useState(null);
  const { inventory: inventoryApi, loading, error } = useSponsorshipApi();

  const queryInventory = useCallback(async (params) => {
    try {
      const response = await inventoryApi.query(params);
      setInventoryData(response);
      return response;
    } catch (err) {
      console.error('Failed to query inventory:', err);
      throw err;
    }
  }, [inventoryApi]);

  return {
    inventoryData,
    loading,
    error,
    queryInventory,
  };
};
